<template>
    <v-container class="offer-detail">
        <v-card class="offer-detail__card" :class="cardClass" elevation="0" rounded="4">
            <v-card-title
                class="offer-top-bar"
                :class="offerHasObservations || offerNeedsModeration ? 'offer-top-bar--grayed' : ''"
            >
                <!-- Avatar -->
                <v-avatar v-if="user.role === 'sysadmin'" class="mr-2" size="60">
                    <v-img :src="getLogo(offer)" />
                </v-avatar>

                <!-- Card Left -->
                <span class="offer-top-bar__left">
                    <v-btn
                        class="pa-0"
                        :to="{
                            name: 'OfferDashboard',
                            params: { slug: offer.slug }
                        }"
                        :disabled="offerHasObservations || offerNeedsModeration || offerIsDisabled"
                        text
                        depressed
                    >
                        <span
                            class="offer-top-bar__title text-truncate"
                            :class="{
                                'offer-detail--disabled': offerIsDisabled
                            }"
                        >
                            {{ getTitle }}
                        </span>
                    </v-btn>
                    <span
                        class="offer-top-bar__date"
                        :class="{
                            'offer-detail--disabled': offerIsDisabled
                        }"
                    >
                        Cierre de postulaciones: {{ formatterDate(offer.validity.end, "ddmmyy") }}
                    </span>
                </span>

                <!-- Card Right -->
                <span class="offer-top-bar__right">
                    <!-- Actions menu -->
                    <v-menu class="actions-menu">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn class="actions-menu__btn" v-bind="attrs" v-on="on" outlined>
                                ACCIONES
                                <v-icon small>mdi-menu-down</v-icon>
                                <v-icon
                                    v-if="!(offerIsPaid || offerIsClosed || offerIsDisabled)"
                                    class="actions-menu__payment-indicator"
                                    small
                                >
                                    mdi-currency-usd
                                </v-icon>
                            </v-btn>
                        </template>

                        <v-list class="actions-menu__list">
                            <v-list-item
                                v-for="(action, index) in actions"
                                :key="index"
                                class="actions-menu__list-item"
                                dense
                                grow
                            >
                                <v-btn
                                    block
                                    tile
                                    class="actions-menu__list-button"
                                    text
                                    :disabled="action.disabled"
                                    @click="action.handler"
                                >
                                    {{ action.title }}
                                </v-btn>
                            </v-list-item>
                        </v-list>
                    </v-menu>

                    <!-- Offer active switch -->
                    <span
                        v-if="offer.status === 'open' || offer.status === 'disable'"
                        class="offer-top-bar__switch-label d-flex align-center"
                        :class="{
                            'offer-top-bar__switch-label--disabled': offer.status === 'open'
                        }"
                    >
                        {{ offer.status === "open" ? "Activo" : "Inactivo" }}
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on" class="info-help">
                                    mdi-information
                                </v-icon>
                            </template>
                            <span>
                                {{ activeSwitchTooltipText }}
                            </span>
                        </v-tooltip>
                        <v-switch
                            class="ml-2"
                            :input-value="state"
                            @change="statusOffer"
                            hide-details
                            :disabled="getSwitchStatus"
                        />
                    </span>
                </span>

                <v-col cols="12" class="pa-0 ma-0 mt-1">
                    <v-divider
                        class="offer-content__divider"
                        :class="{
                            'offer-detail--disabled': offerIsDisabled
                        }"
                    />
                </v-col>
            </v-card-title>

            <!-- Offer content [Approved] -->
            <RouterLink
                v-if="offerIsApproved || offerIsClosed || offerIsDisabled"
                class="offer-content__anchor"
                :to="`/organization/offer/${this.offer.slug}`"
                tag="button"
                :disabled="offerIsDisabled"
            >
                <v-card-text
                    class="offer-content d-flex"
                    :class="{
                        'offer-content--disabled': offerIsDisabled
                    }"
                >
                    <OfferStat
                        title="Postulantes"
                        :number="offerStats.sent"
                        :disabled="offerIsDisabled"
                    />
                    <v-divider
                        class="offer-content__divider--vertical"
                        :class="{
                            'offer-detail--disabled': offerIsDisabled
                        }"
                        vertical
                    />
                    <OfferStat
                        title="Descartados"
                        :number="offerStats.discarded"
                        :disabled="offerIsDisabled"
                    />
                    <v-divider
                        class="offer-content__divider--vertical"
                        :class="{
                            'offer-detail--disabled': offerIsDisabled
                        }"
                        vertical
                    />
                    <OfferStat
                        title="Entrevistas"
                        :number="offerStats.interviewing"
                        :disabled="offerIsDisabled"
                    />
                    <v-divider
                        class="offer-content__divider--vertical"
                        :class="{
                            'offer-detail--disabled': offerIsDisabled
                        }"
                        vertical
                    />
                    <OfferStat
                        title="Seleccionados"
                        :number="offerStats.selected"
                        :disabled="offerIsDisabled"
                    />
                </v-card-text>
            </RouterLink>

            <!-- Offer content [Observations] -->
            <v-card-text
                v-else-if="offerHasObservations"
                class="offer-content offer-content--grayed"
            >
                <v-card class="offer-content__warning-card">
                    <h3 class="offer-content__warning-title">
                        Se encontraron observaciones en la oferta.
                    </h3>
                    <v-btn
                        class="offer-content__warning-btn"
                        :to="`/offer/edit/${offer.slug}`"
                        outlined
                    >
                        MODIFICA AQUÍ
                    </v-btn>
                </v-card>
            </v-card-text>

            <v-card-text
                v-else-if="offerNeedsModeration"
                class="offer-content offer-content--grayed"
            >
                <v-card class="offer-content__warning-card">
                    <h3 class="offer-content__warning-title">
                        Estamos revisando tu oferta, te avisaremos si tenemos observaciones o queda
                        aprobada
                    </h3>
                </v-card>
            </v-card-text>
        </v-card>

        <!-- Expiration date -->
        <div
            v-if="user.role !== 'sysadmin' && offer.status === 'open'"
            class="expiration d-flex align-start mt-2"
        >
            <v-icon class="mr-1" color="#3badff" size="20">mdi-alert-outline</v-icon>
            <p class="expiration__text ma-0 pa-0">
                <small>
                    Quedan
                    <span class="expiration__text--bold">
                        {{ closePostulations(offer.validity.end) }} días
                    </span>
                    para el cierre de postulaciones.
                </small>
                <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" class="info-help info-help">
                            mdi-information-outline
                        </v-icon>
                    </template>
                    <span class="tooltip-txt">
                        Si quieres extender el plazo puedes editar tu oferta, siempre que no supere
                        los 60 días
                    </span>
                </v-tooltip>
            </p>
        </div>

        <!-- Notification -->
        <v-snackbar
            class="offer-detail__snackbar"
            v-model="snackbar.toggled"
            color="#007ECB"
            :timeout="snackbar.timeout"
            top
        >
            <v-icon>mdi-{{ snackbar.icon }}</v-icon>

            <span class="offer-detail__snackbar-text ml-2">
                {{ snackbar.text }}
            </span>

            <template v-slot:action="{ attrs }">
                <v-btn color="white" text v-bind="attrs" @click="snackbar.toggled = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
        <CloseOffer
            :dialog="dialog"
            :offer="offer"
            @decline="dialog = false"
            @closeOfferNow="closeNow"
        />
    </v-container>
</template>

<script>
import { mapActions } from "vuex";
import { currentImg, datetime, tools } from "@/libs/mixins";
import OfferStat from "@/components/OfferStat";
import CloseOffer from "@/components/CloseOffer";

export default {
    name: "OfferDetail",

    components: {
        OfferStat,
        CloseOffer
    },

    props: ["offer", "user"],

    mixins: [currentImg, datetime, tools],

    data() {
        return {
            dialog: false,
            snackbar: {
                text: "",
                timeout: 3000,
                toggled: false
            }
        };
    },

    computed: {
        actions() {
            return [
                {
                    title: "Editar oferta",
                    disabled: this.offerNeedsModeration || this.offerIsClosed,
                    handler: this.onEditOffer
                },
                {
                    title: "Revisar proceso",
                    disabled: this.offerNeedsModeration || this.offerHasObservations,
                    handler: this.onViewOfferDetails
                },
                {
                    title: "Cerrar proceso",
                    disabled:
                        this.offerNeedsModeration ||
                        this.offerHasObservations ||
                        this.offerIsClosed,
                    handler: this.onCloseOffer
                },
                {
                    title: "Eliminar oferta",
                    disabled: this.offerIsApproved || this.offerIsDisabled || this.offerIsClosed,
                    handler: this.onDeleteOffer
                },
                {
                    title: "Pagar oferta",
                    disabled: this.offerIsPaid || this.offerIsClosed,
                    handler: this.onPayOffer
                },
                {
                    title: "Duplicar oferta",
                    disabled: this.offerNeedsModeration,
                    handler: this.onDuplicateOffer
                }
            ];
        },
        activeSwitchTooltipText() {
            return this.offer.status === "open"
                ? "Tu oferta está visible para postulantes."
                : "Tu oferta no está visible para postulantes.";
        },
        cardClass() {
            return this.offerIsApproved
                ? ""
                : this.offerHasObservations
                    ? "offer-detail__card--observations"
                    : this.offerNeedsModeration
                        ? "offer-detail__card--moderation"
                        : "";
        },
        expired() {
            const [year, month, day] = this.offer.validity.end.split("-");
            // la oferta no caduca hasta que termine el día.
            const end = new Date(year, month - 1, day, 23, 59, 59);
            const now = new Date();
            return end >= now;
        },
        getSwitchStatus() {
            if (this.offer.status === "locked") return true;
            if (!this.expired && this.offer.status !== "review") {
                return true;
            }
            return false;
        },
        getTitle() {
            return `${this.offer.title.charAt(0).toUpperCase()}${this.offer.title
                .slice(1)
                .toLowerCase()}`;
        },
        getRem() {
            if (this.breakpoint === "lg") return "width: 30rem;";
            if (this.breakpoint === "md") return "width: 30rem;";
            return "";
        },
        getRemAdmin() {
            if (this.breakpoint === "lg") return "width: 26rem;";
            if (this.breakpoint === "md") return "width: 25rem;";
            return "";
        },
        offerHasObservations() {
            return this.offer.status === "failed";
        },
        offerIsApproved() {
            return this.offer.status === "open";
        },
        offerIsClosed() {
            return this.offer.status === "close";
        },
        offerIsDisabled() {
            return this.offer.status === "disable";
        },
        offerIsModerated() {
            return this.offerHasObservations || this.offerIsApproved; // TODO: Logica de oferta moderada
        },
        offerIsPaid() {
            return this.offer.tabulated;
        },
        offerNeedsModeration() {
            return this.offer.status === "review";
        },
        offerStats() {
            if (this.offer.postulations) {
                return {
                    sent: this.offer.postulations.filter(i => i.status.private === "sent").length,
                    discarded: this.offer.postulations.filter(i => i.status.private === "discarded")
                        .length,
                    interviewing: this.offer.postulations.filter(
                        i => i.status.private === "interviewing"
                    ).length,
                    selected: this.offer.postulations.filter(i => i.status.private === "selected")
                        .length
                };
            } else {
                return {
                    sent: 0,
                    discarded: 0,
                    interviewing: 0,
                    selected: 0
                };
            }
        },
        state() {
            return this.offer.status === "open" ? true : false;
        }
    },

    methods: {
        ...mapActions("offers", ["deleteOffer"]),
        closeNow(value) {
            this.$emit("close", this.offer);
        },
        onCloseOffer() {
            // this.$emit("close", this.offer);
            this.dialog = true;
        },
        async onDeleteOffer() {
            let response = await this.deleteOffer(this.offer);
            if (response.status === "ok") {
                this.showSnackBar("check-bold", "Oferta eliminada exitosamente");
            } else {
                this.showSnackBar("alert", "Hubo un problema al eliminar la oferta");
            }
        },
        async onDuplicateOffer() {
            const duplicatedOffer = JSON.parse(JSON.stringify(this.offer));
            duplicatedOffer.id = null;
            duplicatedOffer.status = "review";
            duplicatedOffer.startAt = "";
            duplicatedOffer.validity = {
                start: "0/0/0",
                end: ""
            };
            duplicatedOffer.moderated_at = null;
            duplicatedOffer.created_at = null;
            duplicatedOffer.updated_at = null;
            try {
                if (duplicatedOffer) {
                    this.$router.push({
                        name: "OfferPost",
                        params: { duplicatedOffer: duplicatedOffer }
                    });
                } else {
                    this.showSnackBar("alert", "Hubo un problema al duplicar la oferta");
                }
            } catch (error) {
                console.error("Error al duplicar la oferta:", error);
                this.showSnackBar("alert", "Hubo un problema al duplicar la oferta");
            }
        },
        onEditOffer() {
            this.$router.push(`/offer/edit/${this.offer.slug}`);
        },
        onPayOffer() {
            this.$router.push("/organization/pricing");
        },
        onViewOfferDetails() {
            this.$router.push(`/organization/offer/${this.offer.slug}`);
        },
        showSnackBar(icon, text) {
            this.snackbar.icon = icon;
            this.snackbar.text = text;
            this.snackbar.toggled = true;
        },
        statusOffer() {
            this.$emit("status", this.offer);
        }
    },

    created() {
        if (this.offer.status !== "close") {
            if (!this.expired && this.offer.status !== "review") this.offer.status = "disable";
        }
    }
};
</script>

<style scoped>
input.search-input label {
    top: 0;
}
</style>
